import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import type { Category } from '@/types/catalog';

interface Props {
  category: Category;
  learned?: number;
  offlineAvailable?: number;
}

/** Barra de progresso da categoria (F8-T05, F15-T07). */
export function CategoryProgress({
  learned,
  total,
}: {
  learned: number;
  total: number;
}): React.ReactElement | null {
  if (total === 0) return null;
  const percent = Math.round((learned / total) * 100);

  return (
    <div className="mt-2">
      <div
        className="h-1 overflow-hidden rounded-full bg-slate-700"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${learned} de ${total} aprendidos`}
      >
        <div className="h-full bg-emerald-400 transition-[width]" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

export function CategoryCard({
  category,
  learned = 0,
  offlineAvailable = 0,
}: Props): React.ReactElement {
  return (
    <li>
      <Link
        to={`/category/${encodeURIComponent(category.id)}`}
        className="flex min-h-16 items-center gap-3 rounded-xl bg-slate-800 px-4 py-3 active:bg-slate-700"
      >
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium">{category.name}</span>

          <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-slate-400">
            <span>{category.actualCount} sinais</span>
            {learned > 0 && <span className="text-emerald-400">{learned} aprendidos</span>}
            {offlineAvailable > 0 && (
              <span className="text-sky-400">{offlineAvailable} offline</span>
            )}
          </span>

          <CategoryProgress learned={learned} total={category.actualCount} />
        </span>

        <ChevronRight size={20} className="shrink-0 text-slate-600" aria-hidden />
      </Link>
    </li>
  );
}
